import React, { useEffect, useMemo, useState } from 'react';
import SlideOutSidebar from 'ui-components/slide-out-sidebar/slideOutSidebar';
import { useSelector } from 'react-redux';
import { AddressState } from "slices/address-manager/AddressState";
import { addressSelector } from "slices/address-manager/addressSlice";
import { useAddressList } from 'features/address-manager/hooks/useAddressList';
import InfiniteScrollableList from 'features/address-manager/common/infiniteScrollableList';
import Loading from 'ui-components/loading/loading';
import LabeledSelectInput from 'ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import { XGSSelectOption } from 'ui-components/xgs-select/xgsSelect';
import './addEditAddressSidebar.scss';

interface FilterSidebarProps {
    isOpen: boolean;
    onClose: () => void;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ isOpen, onClose }) => {
    const addressManagerState: AddressState = useSelector(addressSelector);
    const address = addressManagerState?.selectedAddress;
    const [filters, setFilters] = useState<XGSSelectOption[]>([]);

    const [city, setCity] = useState("");
    const [zip, setZip] = useState("");

    const { data, fetchNextPage, hasNextPage, isLoading } = useAddressList(
        "All Terminals",
        "",
        city,
        "",
        zip
    );


    const items = useMemo(() => data?.pages.flatMap((page) => page.content) ?? [], [data]);
    const itemCount = hasNextPage ? items.length + 1 : items.length;

    const isItemLoaded = useMemo(() => (index: number) => index < items.length, [items]);

    const loadMoreItems = async (startIndex: number, stopIndex: number) => {
        if (hasNextPage) {
            fetchNextPage();
        }
    };

    const Row = React.useCallback(({ index, style }) => {
        if (!isItemLoaded(index)) {
            return <div style={{ ...style, padding: 10 }}>Loading...</div>;
        }

        const item = items[index];
        return (
            <div style={style}>
                <ul className="filter-sidebar__content__card">
                    <li>
                        <div className="filter-sidebar__content__card__address__line1">{item.line1}</div>
                        <div className="filter-sidebar__content__card__address__line2">{`${item.city.toLowerCase().capitalize()}, ${item.state} ${item.zip}`}</div>
                    </li>
                </ul>
            </div>
        );
    }, [items, isItemLoaded]);


    // when address changes, update filters
    useEffect(() => {
        if (address) {
            setCity(address.city ?? "");
            setZip(address.zip ?? "");
            setFilters(
                [
                    {
                        label: "City: " + address?.city || "",
                        value: address?.city || ""
                    },
                    {
                        label: "ZIP: " + address?.zip || "",
                        value: address?.zip || ""
                    }
                ]
            );
        }
    }, [address]);

    const getFilterOptions = () => {
        return [
            {
                label: "City: " + address?.city || "",
                value: address?.city || ""
            },
            {
                label: "ZIP: " + address?.zip || "",
                value: address?.zip || ""
            }
        ];
    };

    const onFilterChange = (v: any) => {
        setFilters(v);
        let cityFilter = v ? v.filter((option: any) => option.label.startsWith("City:")) : [];
        let zipFilter = v ? v.filter((option: any) => option.label.startsWith("ZIP:")) : [];
        setCity(cityFilter[0]?.value);
        setZip(zipFilter[0]?.value);
    };

    const header = useMemo(() => "Addresses" + (filters?.length > 0 ? " in " : "")
        + (filters?.[0]?.value ?? "") + (filters?.length > 1 ? ", " + (filters?.[1]?.value ?? "") : "")
        + " (" + itemCount + " Result" + (itemCount > 1 ? "s" : "") + ")", [filters, itemCount]);
    return (
        <SlideOutSidebar
            show={isOpen}
            onClose={onClose}
            header={header}
            size="small"
            className="filter-sidebar"
        >
            <div className="filter-sidebar__content">
                <div className="filter-sidebar__content__title">
                    Filtered Results
                </div>
                <div className="filter-sidebar__content__tags">
                    <LabeledSelectInput
                        onMultiValuesChange={(option) => onFilterChange(option)}
                        value={filters}
                        options={getFilterOptions()}
                        labelMode={LabelModes.row}
                        className="xgs-pickup-assignment__orders__filters__control__input xgs-pickup-assignment__orders__filters__control__input--wide"
                        isClearable={false}
                        isMulti
                        disabled={false}
                    />
                </div>
                <div className="filter-sidebar__content__scrollable_list">
                    <div style={{ flex: 1 }}>
                        {isLoading ? (
                            <div className="xgs-sideaddress-list__loading"><Loading isLoading={true} /></div>
                        ) : (
                            <InfiniteScrollableList
                                isItemLoaded={isItemLoaded}
                                itemCount={itemCount}
                                loadMoreItems={loadMoreItems}
                                children={Row}
                                itemSize={70}
                            />
                        )}
                    </div>
                </div>

            </div>
        </SlideOutSidebar>
    );
};

export default FilterSidebar;
